import { useEffect, useRef } from 'react';
import { ChevronLeft, X } from 'lucide-react';

import { Launcher } from '@/components/app-shell/support/Launcher';
import { supportChat } from '@/components/app-shell/support/supportChatUi';
import {
  NewProviderConversation,
  ProviderThreadConversation,
  type ProviderRouteCheck,
} from '@/features/clinic/components/MessageDrawer';
import { mostRecentThread } from '@/features/clinic/ClinicScreen';
import { messageDrawer } from '@/features/clinic/stores/messageDrawer';
import { nativeSupportEnabled } from '@/features/native-support/types';
import {
  useBootstrap,
  useMessagingRouteCheck,
  useRealtimeSupportMessages,
  useSendSupportMessage,
  useSupportThread,
  useThreads,
} from '@/lib/api/hooks';
import { ArrowInline } from '@/lib/Icon';
import { cn } from '@/lib/cn';
import type { MessagingRouteResponse } from '@/types/api';

import { messagingHub, useMessagingHub, type Team } from './messagingHub';
import { useMessagingUnread } from './useMessagingUnread';
import { SupportInfo, SupportPane } from './components/SupportPane';
import {
  ProviderAvatars,
  SupportAvatar,
  providerNames,
  useProviders,
  type Provider,
} from './components/TeamAvatars';

// A routing check on every tiny message ("ok", "thanks!") only adds latency.
const MIN_ROUTE_CHECK_CHARS = 12;
// The live triage classifier takes ~4 s, so the check starts once the
// patient pauses typing; on send we wait at most this long for it.
const ROUTE_PREFETCH_PAUSE_MS = 900;
const ROUTE_CHECK_TIMEOUT_MS = 7_000;

/**
 * Care-team messaging (variant-a of `care-team-messaging-v1`): the chat
 * launcher plus one Messages drawer for both teams. Mounted by AppLayout
 * INSTEAD of the clinic MessageDrawer + SupportChat; while mounted it
 * takes over their stores so every existing entry point lands here.
 */
export function MessagesHub() {
  const hub = useMessagingHub();
  const unread = useMessagingUnread();
  const { data: supportThread } = useSupportThread();
  useRealtimeSupportMessages(supportThread?.thread_id);

  useEffect(() => {
    supportChat.setDelegate(({ chooser }) =>
      messagingHub.open({
        team: chooser ? null : 'support',
        source: chooser ? 'help_link' : 'support_link',
      }),
    );
    messageDrawer.setDelegate(() =>
      messagingHub.open({ team: 'providers', source: 'clinician_link' }),
    );
    return () => {
      supportChat.setDelegate(null);
      messageDrawer.setDelegate(null);
      messagingHub.close();
    };
  }, []);

  return (
    <>
      {!hub.open && (
        <Launcher
          unreadCount={unread.total}
          label="Messages"
          onClick={() => messagingHub.open({ source: 'launcher' })}
        />
      )}
      <MessagesDrawer unread={unread} />
    </>
  );
}

function MessagesDrawer({ unread }: { unread: { providers: number; support: number } }) {
  const hub = useMessagingHub();
  const { data: threads } = useThreads();
  const thread = mostRecentThread(threads ?? []);
  const providers = useProviders();

  return (
    <>
      {hub.open && (
        <button
          type="button"
          aria-label="Close messages"
          onClick={() => messagingHub.close()}
          className="fixed inset-0 bg-black/10 backdrop-blur-[10px] z-helpdock"
        />
      )}
      <aside
        aria-hidden={!hub.open}
        aria-label="Messages"
        className={cn(
          'fixed inset-x-0 top-45 bottom-0 md:top-0 md:right-0 md:left-auto md:w-[420px] z-drawer',
          'bg-[var(--surface-page)] border-l border-line-subtle flex flex-col',
          'transition-transform duration-base ease-ease',
          hub.open
            ? 'translate-y-0 md:translate-x-0'
            : 'translate-y-full md:translate-y-0 md:translate-x-full pointer-events-none',
        )}
      >
        {hub.open && hub.team === null && <Chooser providers={providers} unread={unread} />}
        {hub.open && hub.team !== null && (
          <>
            <TeamHeader team={hub.team} unread={unread} />
            {hub.team === 'providers' ? (
              <ProvidersPane threadId={thread?.id ?? null} providers={providers} />
            ) : (
              <SupportPane providers={providers} />
            )}
          </>
        )}
        {!hub.open && <div className="flex-1" />}
      </aside>
    </>
  );
}

function CloseButton() {
  return (
    <button
      type="button"
      onClick={() => messagingHub.close()}
      aria-label="Close"
      className="w-45 h-45 shrink-0 flex items-center justify-center border-l border-line-subtle bg-slate text-ink-secondary hover:text-ink"
    >
      <X size={18} strokeWidth={1.5} />
    </button>
  );
}

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="inline-flex items-center justify-center min-w-18 h-18 px-5 rounded-pill bg-accent text-white type-data-xs">
      {count > 9 ? '9+' : count}
    </span>
  );
}

/** "Who would you like to reach?" — the bubble's landing view. */
function Chooser({
  providers,
  unread,
}: {
  providers: Provider[];
  unread: { providers: number; support: number };
}) {
  const { data: bootstrap } = useBootstrap();
  const firstName = bootstrap?.patient.first_name?.trim();
  return (
    <>
      <header className="flex items-center justify-between bg-[var(--surface-sunken)] border-b border-line-subtle pr-2">
        <h2 className="font-mono uppercase text-13 text-ink-secondary px-15 py-15">Messages</h2>
        <CloseButton />
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto px-15 pt-30 pb-15 flex flex-col gap-20">
        <h3 className="type-headline-sm">
          {firstName ? `Hi ${firstName},` : 'Hi,'}
          <br />
          who would you like to reach?
        </h3>
        <TeamRow
          label="Providers"
          unread={unread.providers}
          avatar={<ProviderAvatars providers={providers} />}
          name={providerNames(providers)}
          description="Symptoms, side effects, dosing and your prescription"
          onClick={() => messagingHub.selectTeam('providers', 'chooser')}
        />
        <TeamRow
          label="System Support"
          unread={unread.support}
          avatar={<SupportAvatar />}
          name="System Support"
          description="Orders, shipping, billing and account. Instant answers."
          info={nativeSupportEnabled() ? <SupportInfo /> : undefined}
          onClick={() => messagingHub.selectTeam('support', 'chooser')}
        />
        <div className="mt-auto pt-15 border-t border-line-subtle flex flex-col gap-6 text-12 text-ink-muted leading-140">
          <p>Medical emergency? Call 911.</p>
          {!nativeSupportEnabled() && (
            <p>
              Prefer email?{' '}
              <a
                href={`mailto:${bootstrap?.help_contact.email ?? 'support@systemlabs.com'}`}
                className="text-ink underline underline-offset-4"
              >
                {bootstrap?.help_contact.email ?? 'support@systemlabs.com'}
              </a>
            </p>
          )}
        </div>
      </div>
    </>
  );
}

function TeamRow({
  label,
  info,
  unread,
  avatar,
  name,
  description,
  onClick,
}: {
  label: string;
  info?: React.ReactNode;
  unread: number;
  avatar: React.ReactNode;
  name: string;
  description: string;
  onClick: () => void;
}) {
  const strip = (
    <span className="flex justify-between border-b border-line-subtle bg-[var(--surface-sunken)]">
      {info ? (
        <span className="flex items-center pl-15 py-10">
          <span className="type-label-sm text-[#7C7C7C]">{label}</span>
          {info}
        </span>
      ) : (
        <span className="type-label-sm text-[#7C7C7C] px-15 py-10">{label}</span>
      )}
      {unread > 0 && (
        <span className="type-label-sm px-10 py-3 min-w-[12rem] flex items-center justify-center bg-[var(--accent-hover)] text-white">
          New message
        </span>
      )}
    </span>
  );
  const body = (
    <span className="flex items-center gap-12 p-15">
      {avatar}
      <span className="flex-1 min-w-0 flex flex-col gap-3">
        <span className="type-body-md text-ink">{name}</span>
        <span className="text-12 text-[#7C7C7C] leading-130">{description}</span>
      </span>
      <ArrowInline className="text-ink-muted" />
    </span>
  );
  // The info icon is its own button, so with it the strip sits outside the row's button.
  if (info) {
    return (
      <div className="w-full flex flex-col border border-line-subtle bg-[var(--surface-raised)]">
        {strip}
        <button type="button" onClick={onClick} className="w-full flex flex-col hover:bg-[var(--surface-sunken)] text-left">
          {body}
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex flex-col border border-line-subtle bg-[var(--surface-raised)] hover:bg-[var(--surface-sunken)] text-left"
    >
      {strip}
      {body}
    </button>
  );
}

/** Back to the chooser · Providers | System Support tabs · close. */
function TeamHeader({
  team,
  unread,
}: {
  team: Team;
  unread: { providers: number; support: number };
}) {
  const tab = (key: Team, label: string, count: number) => (
    <button
      type="button"
      aria-pressed={team === key}
      onClick={() => messagingHub.selectTeam(key, 'tabs')}
      // Plain join, not cn(): tailwind-merge reads text-12 and a text colour
      // as the same group and would drop the size.
      className={[
        'h-45 flex items-center justify-center gap-6 font-mono uppercase text-12 border-b-2',
        team === key
          ? 'bg-[var(--surface-page)] text-ink border-line-strong'
          : 'text-[#7C7C7C] border-transparent hover:text-ink',
      ].join(' ')}
    >
      {label}
      <UnreadBadge count={team === key ? 0 : count} />
    </button>
  );
  return (
    <header className="shrink-0 flex items-stretch h-45 bg-[var(--surface-sunken)] border-b border-line-subtle">
      <button
        type="button"
        aria-label="All messages"
        onClick={() => messagingHub.backToChooser()}
        className="w-45 shrink-0 flex items-center justify-center border-r border-line-subtle text-ink-secondary hover:text-ink"
      >
        <ChevronLeft size={18} strokeWidth={1.5} />
      </button>
      <nav aria-label="Choose who to message" className="flex-1 grid grid-cols-2 [&>*+*]:border-l [&>*+*]:border-l-line-subtle">
        {tab('providers', 'Providers', unread.providers)}
        {tab('support', 'System Support', unread.support)}
      </nav>
      <CloseButton />
    </header>
  );
}

function ProvidersPane({
  threadId,
  providers,
}: {
  threadId: string | null;
  providers: Provider[];
}) {
  const routeCheck = useMessagingRouteCheck();
  // One check per distinct draft: started when the patient pauses typing,
  // reused on send so the classifier's latency is usually already paid.
  const verdicts = useRef(new Map<string, Promise<MessagingRouteResponse | null>>());
  const pause = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(pause.current), []);
  const verdictFor = (text: string) => {
    let pending = verdicts.current.get(text);
    if (!pending) {
      pending = routeCheck.mutateAsync(text).catch(() => null);
      verdicts.current.set(text, pending);
    }
    return pending;
  };
  const onDraftChange = (draft: string) => {
    clearTimeout(pause.current);
    const text = draft.trim();
    if (text.length < MIN_ROUTE_CHECK_CHARS) return;
    pause.current = setTimeout(() => void verdictFor(text), ROUTE_PREFETCH_PAUSE_MS);
  };
  // Advisory only: a slow or failed check sends to providers as normal.
  const check: ProviderRouteCheck = async (text) => {
    if (text.length < MIN_ROUTE_CHECK_CHARS) return null;
    clearTimeout(pause.current);
    const verdict = await Promise.race([
      verdictFor(text),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), ROUTE_CHECK_TIMEOUT_MS)),
    ]);
    return verdict?.suggest_team === 'support' ? { topic: verdict.topic } : null;
  };
  // "Send to System Support instead" sends it there for the patient and
  // shows the Support chat, where the AI picks it up.
  const sendSupport = useSendSupportMessage();
  const toSupport = (text: string) => {
    sendSupport.mutate(text);
    messagingHub.selectTeam('support', 'route_nudge');
  };

  return (
    <>
      <div className="shrink-0 flex items-center gap-10 px-15 py-12 border-b border-line-subtle bg-[var(--surface-raised)]">
        <ProviderAvatars providers={providers} />
        <div className="min-w-0">
          <div className="text-13 leading-120">{providerNames(providers)}</div>
          <div className="text-12 text-ink-muted leading-130 mt-2">
            Symptoms, side effects, dosing and your prescription
          </div>
        </div>
      </div>
      {threadId ? (
        <ProviderThreadConversation
          key={threadId}
          threadId={threadId}
          routeCheck={check}
          onRouteToSupport={toSupport}
          onDraftChange={onDraftChange}
          placeholder="Message your providers…"
        />
      ) : (
        <NewProviderConversation
          routeCheck={check}
          onRouteToSupport={toSupport}
          onDraftChange={onDraftChange}
          placeholder="Message your providers…"
        />
      )}
    </>
  );
}
